import React from "react";
import SceneGraphCanvasContextMenu from "./SceneGraphCanvasContextMenu";
import SceneGraphBodyContextMenu from "./SceneGraphBodyContextMenu";
import SceneGraphObjectContextMenu from "./SceneGraphObjectContextMenu";
import SceneGraphJointContextMenu from "./SceneGraphJointContextMenu";
import SceneGraphMeshContextMenu from "./SceneGraphMeshContextMenu";
import SceneGraphSiteContextMenu from "./SceneGraphSiteContextMenu";
import SceneGraphCameraContextMenu from "./SceneGraphCameraContextMenu";
import SceneGraphInertialContextMenu from "./SceneGraphInertialContextMenu";
import SceneGraphGeomContextMenu from "./SceneGraphGeomContextMenu";
import { useSceneGraphContextMenuModel } from "./SceneGraphContextMenuContext";

export default function SceneGraphContextMenu() {
  const {
    state: {
      contextMenuOpen,
      contextMenuType,
      contextMenuBodyUuid,
      contextMenuBodyUuids,
      contextMenuJointIds,
      contextMenuMeshIds,
      contextMenuGeomKeys,
      contextMenuSiteIds,
      contextMenuCameraIds,
      contextMenuInertialKeys,
      contextMenuRef,
      contextMenuPosition,
      contextMenuGeomSelectionLabel,
      contextMenuDirectCollisionGeomKey,
      contextMenuInertialSourceBodyUuids,
    },
    submenus: {
      deleteSubmenuOpen,
      duplicateSubmenuOpen,
      addNewSubmenuOpen,
      setAddNewSubmenuOpen,
      addJointKindSubmenuOpen,
      setAddJointKindSubmenuOpen,
      addGeomKindSubmenuOpen,
      setAddGeomKindSubmenuOpen,
      addVisualGeomKindSubmenuOpen,
      setAddVisualGeomKindSubmenuOpen,
      reparentSubmenuOpen,
      setBodyActionSubmenuKey,
      setJointActionSubmenuKey,
      setReparentOnlySubmenuKey,
      closeContextSubmenus,
      setContextMenuOpen,
    },
    scene: {
      isPhysicsRunning,
      worldRootTargetBodyUuid,
      effectiveMoveBodyUuids,
      invalidBatchParentUuids,
      groupedMoveParentOptions,
      groupedMeshParentOptions,
      groupedInertialParentOptions,
      bodies,
    },
    visibility: {
      viewerActions,
      bodyVisibility,
      meshVisibility,
      siteVisibility,
      collisionGeomVisibility,
      inertialVisibility,
      jointVizVisibility,
    },
    actions: {
      inspectContextMenuTarget,
      handleAddRootBody,
      handleAddChildBody,
      handleAddJointToBody,
      handleAddSiteToBody,
      handleAddGeomToBody,
      handleAddVisualGeomToBody,
      handleAddConstraintForBody,
      handleAddConstraintForJoint,
      handleAddTendonForJoint,
      handleAddCameraToBody,
      handleAddFixedWorldCamera,
      handleAddActuatorForJoint,
      handleDuplicateBody,
      handleDuplicateCollision,
      handleDeleteBodyObjects,
      handleDeleteBodies,
      handleDeleteJoints,
      handleDeleteMeshes,
      handleDeleteGeoms,
      handleDeleteSites,
      handleDeleteCameras,
      handleDeleteInertials,
      handleMoveBodies,
      handleMoveJointsToBody,
      handleMoveMeshesToBody,
      handleMoveGeomsToBody,
      handleMoveSitesToBody,
      handleMoveCamerasToBody,
      handleMoveInertialsToBody,
    },
    options: {
      getJointAddOptionsForBody,
      getPreferredTendonJointForBody,
      renderJointReparentBodyButton,
    },
  } = useSceneGraphContextMenuModel();
  const canvasMenuProps = {
    addNewSubmenuOpen,
    setAddNewSubmenuOpen,
    addJointKindSubmenuOpen,
    setAddJointKindSubmenuOpen,
    addGeomKindSubmenuOpen,
    setAddGeomKindSubmenuOpen,
    addVisualGeomKindSubmenuOpen,
    setAddVisualGeomKindSubmenuOpen,
    isPhysicsRunning,
    worldRootTargetBodyUuid,
    handleAddRootBody,
    handleAddJointToBody,
    handleAddSiteToBody,
    handleAddGeomToBody,
    handleAddVisualGeomToBody,
    handleAddConstraintForBody,
    handleAddTendonForJoint,
    handleAddFixedWorldCamera,
    getPreferredTendonJointForBody,
  };

  const objectMenuProps = {
    contextMenuBodyUuid,
    isPhysicsRunning,
    inspectContextMenuTarget,
    handleDuplicateBody,
    handleDeleteBodyObjects,
  };

  const bodyMenuProps = {
    contextMenuBodyUuids,
    effectiveMoveBodyUuids,
    invalidBatchParentUuids,
    deleteSubmenuOpen,
    duplicateSubmenuOpen,
    addNewSubmenuOpen,
    addJointKindSubmenuOpen,
    setAddJointKindSubmenuOpen,
    addGeomKindSubmenuOpen,
    setAddGeomKindSubmenuOpen,
    addVisualGeomKindSubmenuOpen,
    setAddVisualGeomKindSubmenuOpen,
    reparentSubmenuOpen,
    setBodyActionSubmenuKey,
    setContextMenuOpen,
    isPhysicsRunning,
    groupedMoveParentOptions,
    bodies,
    viewerActions,
    bodyVisibility,
    inspectContextMenuTarget,
    handleAddChildBody,
    handleAddJointToBody,
    handleAddSiteToBody,
    handleAddGeomToBody,
    handleAddVisualGeomToBody,
    handleAddConstraintForBody,
    handleAddTendonForJoint,
    handleAddCameraToBody,
    handleDuplicateBody,
    handleDeleteBodyObjects,
    handleDeleteBodies,
    handleMoveBodies,
    getJointAddOptionsForBody,
    getPreferredTendonJointForBody,
  };

  const jointMenuProps = {
    contextMenuJointIds,
    addNewSubmenuOpen,
    reparentSubmenuOpen,
    setJointActionSubmenuKey,
    closeContextSubmenus,
    setContextMenuOpen,
    isPhysicsRunning,
    worldRootTargetBodyUuid,
    groupedMeshParentOptions,
    bodies,
    viewerActions,
    jointVizVisibility,
    inspectContextMenuTarget,
    handleDeleteJoints,
    handleMoveJointsToBody,
    handleAddActuatorForJoint,
    handleAddConstraintForJoint,
    handleAddTendonForJoint,
    renderJointReparentBodyButton,
  };

  const meshMenuProps = {
    contextMenuMeshIds,
    reparentSubmenuOpen,
    setReparentOnlySubmenuKey,
    closeContextSubmenus,
    setContextMenuOpen,
    isPhysicsRunning,
    worldRootTargetBodyUuid,
    groupedMeshParentOptions,
    bodies,
    viewerActions,
    meshVisibility,
    inspectContextMenuTarget,
    handleDeleteMeshes,
    handleMoveMeshesToBody,
  };

  const siteMenuProps = {
    contextMenuSiteIds,
    reparentSubmenuOpen,
    setReparentOnlySubmenuKey,
    closeContextSubmenus,
    setContextMenuOpen,
    isPhysicsRunning,
    worldRootTargetBodyUuid,
    groupedMeshParentOptions,
    bodies,
    viewerActions,
    siteVisibility,
    inspectContextMenuTarget,
    handleDeleteSites,
    handleMoveSitesToBody,
  };

  const cameraMenuProps = {
    contextMenuCameraIds,
    reparentSubmenuOpen,
    setReparentOnlySubmenuKey,
    closeContextSubmenus,
    isPhysicsRunning,
    worldRootTargetBodyUuid,
    groupedMeshParentOptions,
    bodies,
    inspectContextMenuTarget,
    handleDeleteCameras,
    handleMoveCamerasToBody,
  };

  const inertialMenuProps = {
    contextMenuInertialKeys,
    contextMenuInertialSourceBodyUuids,
    reparentSubmenuOpen,
    setReparentOnlySubmenuKey,
    closeContextSubmenus,
    setContextMenuOpen,
    isPhysicsRunning,
    worldRootTargetBodyUuid,
    groupedInertialParentOptions,
    bodies,
    viewerActions,
    inertialVisibility,
    inspectContextMenuTarget,
    handleDeleteInertials,
    handleMoveInertialsToBody,
  };

  const geomMenuProps = {
    contextMenuGeomKeys,
    contextMenuGeomSelectionLabel,
    contextMenuDirectCollisionGeomKey,
    reparentSubmenuOpen,
    setReparentOnlySubmenuKey,
    closeContextSubmenus,
    setContextMenuOpen,
    isPhysicsRunning,
    worldRootTargetBodyUuid,
    groupedMeshParentOptions,
    bodies,
    viewerActions,
    collisionGeomVisibility,
    inspectContextMenuTarget,
    handleDuplicateCollision,
    handleDeleteGeoms,
    handleMoveGeomsToBody,
  };

  const renderContextMenuContent = () => {
    switch (contextMenuType) {
      case "canvas":
        return <SceneGraphCanvasContextMenu {...canvasMenuProps} />;
      case "object":
        return <SceneGraphObjectContextMenu {...objectMenuProps} />;
      case "body":
        return <SceneGraphBodyContextMenu {...bodyMenuProps} />;
      case "joint":
        return <SceneGraphJointContextMenu {...jointMenuProps} />;
      case "mesh":
        return <SceneGraphMeshContextMenu {...meshMenuProps} />;
      case "site":
        return <SceneGraphSiteContextMenu {...siteMenuProps} />;
      case "camera":
        return <SceneGraphCameraContextMenu {...cameraMenuProps} />;
      case "inertial":
        return <SceneGraphInertialContextMenu {...inertialMenuProps} />;
      default:
        return <SceneGraphGeomContextMenu {...geomMenuProps} />;
    }
  };

  return (
    <>
      {contextMenuOpen &&
        (contextMenuType === "canvas" ||
          (contextMenuType === "object" && contextMenuBodyUuid) ||
          (contextMenuType === "body" && contextMenuBodyUuids.length > 0) ||
          (contextMenuType === "joint" && contextMenuJointIds.length > 0) ||
          (contextMenuType === "mesh" && contextMenuMeshIds.length > 0) ||
          (contextMenuType === "geom" && contextMenuGeomKeys.length > 0) ||
          (contextMenuType === "site" && contextMenuSiteIds.length > 0) ||
          (contextMenuType === "camera" && contextMenuCameraIds.length > 0) ||
          (contextMenuType === "inertial" && contextMenuInertialKeys.length > 0)) && (
          <div
            ref={contextMenuRef}
            className={`absolute w-fit rounded-md border p-0.5 shadow-xl z-[10060] ${
              isPhysicsRunning ? "border-amber-400/70 shadow-amber-950/30" : "border-gray-600"
            }`}
            style={{
              backgroundColor: "rgba(40, 40, 40, 0.95)",
              left: `${contextMenuPosition.x}px`,
              top: `${contextMenuPosition.y}px`,
            }}
          >
            {renderContextMenuContent()}
          </div>
        )}
    </>
  );
}
